import React, { useState, useContext, useEffect } from "react";
import {
  Card,
  Form,
  Input,
  Select,
  DatePicker,
  Button,
  Typography,
  Row,
  Col,
  Statistic,
  message,
  InputNumber,
  Tabs,
  Space,
  Table,
  Tag,
  Alert,
} from "antd";
import {
  PlusCircleOutlined,
  MinusCircleOutlined,
  DollarOutlined,
  BankOutlined,
  MobileOutlined,
  SwapOutlined,
  SyncOutlined,
  ExclamationCircleOutlined,
} from "@ant-design/icons";
import axios from "axios";
import { AppContext } from "../context/AppContext";
import { useLocation } from "react-router-dom";
import moment from "moment";
import AccountsService from "../utils/accountsService";
import { requiresApproval } from "../config/expenseApproval";
import { TransactionHooks } from "../utils/transactionHooks";

const { Title, Text } = Typography;
const { Option } = Select;
const { TabPane } = Tabs;

// Zambian colors
const ZAMBIAN_COLORS = {
  green: "#078930",
  red: "#EF3340",
  black: "#000000",
  orange: "#FFB81C",
};

const FloatManagementPage = () => {
  const [form] = Form.useForm();
  const [transferForm] = Form.useForm();
  const [submitting, setSubmitting] = useState(false);
  const [transferSubmitting, setTransferSubmitting] = useState(false);
  const location = useLocation();
  const { company, accounts, updateAccountBalance, updateAccounts, user } =
    useContext(AppContext);
  const [selectedAccount, setSelectedAccount] = useState(null);
  const [isTransferModalVisible, setIsTransferModalVisible] = useState(false);
  const [refreshingAccounts, setRefreshingAccounts] = useState(false);
  const [transactions, setTransactions] = useState([]);
  const [loadingTransactions, setLoadingTransactions] = useState(false);
  const token = localStorage.getItem("token");

  // Function to refresh all accounts data
  const refreshAllAccounts = async () => {
    try {
      setRefreshingAccounts(true);
      if (company?.id) {
        const updatedAccounts = await AccountsService.fetchAccounts(
          company.id,
          token
        );
        // Update the accounts in the context - this will refresh the navbar display
        updateAccounts(updatedAccounts);

        // Also update selected account if applicable
        if (selectedAccount) {
          const updated = updatedAccounts.find(
            (acc) => acc.id === selectedAccount.id
          );
          if (updated) {
            setSelectedAccount(updated);
          }
        }
      }
    } catch (error) {
      console.error("Failed to refresh accounts:", error);
    } finally {
      setRefreshingAccounts(false);
    }
  };

  // Function to fetch transactions for the selected account
  const fetchAccountTransactions = async () => {
    if (!selectedAccount || !company?.id) return;

    try {
      setLoadingTransactions(true);

      // First, let's try to get all company transactions without account filtering
      console.log("Fetching transactions for company:", company.id);

      const response = await axios.get(
        "https://zedlenders.pickmesms.com/items/transactions",
        {
          headers: { Authorization: `Bearer ${token}` },
          params: {
            filter: {
              company: company.id,
            },
            sort: ["-date_created", "-transaction_date"],
            limit: 50,
          },
        }
      );

      console.log("API Response:", response.data);
      console.log("Selected account ID:", selectedAccount.id);

      // Filter transactions client-side for now
      const allTransactions = response.data.data || [];
      const filteredTransactions = allTransactions.filter((transaction) => {
        // Include if this account is involved in any way
        return (
          transaction.account === selectedAccount.id ||
          transaction.from_account === selectedAccount.id ||
          transaction.to_account === selectedAccount.id
        );
      });

      console.log("All transactions:", allTransactions.length);
      console.log("Filtered transactions:", filteredTransactions.length);
      console.log("Sample transaction:", allTransactions[0]);
      console.log(
        "Sample transaction keys:",
        Object.keys(allTransactions[0] || {})
      );

      // Debug: Check what field names exist and their values
      if (allTransactions.length > 0) {
        const sample = allTransactions[0];
        console.log("Account field value:", sample.account);
        console.log("From_account field value:", sample.from_account);
        console.log("To_account field value:", sample.to_account);
        console.log("Looking for account ID:", selectedAccount.id);
        console.log("Account ID type:", typeof selectedAccount.id);
      }

      setTransactions(filteredTransactions.slice(0, 20));
    } catch (error) {
      console.error("Failed to fetch transactions:", error);
      console.error("Error details:", error.response?.data);
      message.error("Failed to load transaction history");
    } finally {
      setLoadingTransactions(false);
    }
  };

  // Set the selected account to the first one by default or to the default account if available
  useEffect(() => {
    if (accounts && accounts.length > 0) {
      const defaultAccount =
        accounts.find((acc) => acc.is_default) || accounts[0];
      setSelectedAccount(defaultAccount);
    }
  }, [accounts]);

  // Fetch transactions when selected account changes
  useEffect(() => {
    if (selectedAccount) {
      fetchAccountTransactions();
    }
  }, [selectedAccount]);

  const TRANSACTION_TYPES = [
    {
      label: "Float Addition",
      value: "Float Addition",
      icon: <PlusCircleOutlined />,
      operation: "add",
    },
    {
      label: "Operational Cost",
      value: "Operational Cost",
      icon: <MinusCircleOutlined />,
      operation: "subtract",
    },
  ];

  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const requestedType = params.get("type") || params.get("transaction_type");
    if (!requestedType) return;

    const match = TRANSACTION_TYPES.find(
      (type) => type.value.toLowerCase() === requestedType.toLowerCase()
    );
    if (match) {
      form.setFieldsValue({ transaction_type: match.value });
    }
  }, [location.search, form]);

  const PAYMENT_METHODS = [
    { label: "Bank Transfer", value: "bank_transfer", icon: <BankOutlined /> },
    { label: "Mobile Money", value: "mobile_money", icon: <MobileOutlined /> },
    { label: "Cash", value: "cash", icon: <DollarOutlined /> },
  ];

  const handleSubmit = async (values) => {
    try {
      if (!selectedAccount) {
        message.error("Please select an account");
        return;
      }

      setSubmitting(true);
      const amount =
        values.transaction_type === "Float Addition"
          ? values.amount
          : -values.amount;

      // Check if this expense requires approval
      const needsApproval = requiresApproval(
        user?.email,
        values.transaction_type,
        values.amount
      );

      // Set transaction status based on approval requirement
      const transactionStatus = needsApproval ? "Pending" : "Completed";

      // First, record the transaction in transactions table
      await axios.post(
        "https://zedlenders.pickmesms.com/items/transactions",
        {
          ...values,
          amount: Math.abs(amount),
          transaction_date: values.transaction_date.format("YYYY-MM-DD"),
          company: company?.id,
          account: selectedAccount.id,
          is_loan_transaction: false,
          transaction_status: transactionStatus,
          new_amount: needsApproval
            ? selectedAccount?.balance || 0 // Don't update balance if pending approval
            : amount + (selectedAccount?.balance || 0),
          created_by: user?.email,
        },
        { headers: { Authorization: `Bearer ${token}` } }
      );

      // Only update the account balance if not requiring approval
      if (!needsApproval) {
        await AccountsService.updateAccount(
          selectedAccount.id,
          {
            balance:
              parseFloat(selectedAccount.balance || 0) + parseFloat(amount),
          },
          token
        );

        // Record double-entry accounting for float addition
        if (values.transaction_type === "Float Addition") {
          try {
            await TransactionHooks.onFloatAdded({
              amount: parseFloat(values.amount || 0),
              date: values.transaction_date.toDate(),
              company: company?.id,
              token,
              accountId: selectedAccount.id,
              source: values.payment_method === "Investment" ? "Investor" : "Owner",
            });
          } catch (doubleEntryError) {
            console.error("Error recording double-entry for float addition:", doubleEntryError);
            // Continue even if double-entry fails
          }
        }

        // Record double-entry accounting for operational costs
        if (values.transaction_type === "Operational Cost") {
          try {
            await TransactionHooks.onExpenseRecorded({
              amount: parseFloat(values.amount || 0),
              description: values.notes || values.description || "Operational expense",
              date: values.transaction_date.toDate(),
              company: company?.id,
              token,
              accountId: selectedAccount.id,
            });
          } catch (doubleEntryError) {
            console.error("Error recording double-entry for expense:", doubleEntryError);
            // Continue even if double-entry fails
          }
        }
      }

      if (needsApproval) {
        message.success(
          "Expense submitted for approval! Administrators will review your request.",
          4
        );
      } else {
        message.success("Transaction recorded successfully!");
      }

      form.resetFields();

      // Refresh all accounts to update the navbar
      await refreshAllAccounts();

      // Refresh transactions to show the new transaction
      await fetchAccountTransactions();
    } catch (error) {
      message.error("Failed to record transaction: " + error.message);
    } finally {
      setSubmitting(false);
    }
  };

  const handleTransfer = async (values) => {
    try {
      if (values.from_account === values.to_account) {
        message.error("Source and destination accounts must be different");
        return;
      }

      setTransferSubmitting(true);

      await AccountsService.transferFloat(
        values.from_account,
        values.to_account,
        values.amount,
        token,
        values.notes
      );

      message.success("Funds transferred successfully");
      transferForm.resetFields();

      // Refresh all accounts to update the navbar and current selection
      await refreshAllAccounts();

      // Refresh transactions to show the new transfer
      await fetchAccountTransactions();
    } catch (error) {
      message.error("Transfer failed: " + error.message);
      console.log("Transfer error:", error);
    } finally {
      setTransferSubmitting(false);
    }
  };

  return (
    <div style={{ padding: 24 }}>
      <Card
        bordered={false}
        style={{
          borderTop: `4px solid ${ZAMBIAN_COLORS.green}`,
          boxShadow: "0 2px 8px rgba(0, 0, 0, 0.09)",
        }}
      >
        <Row gutter={16} align="middle" style={{ marginBottom: 24 }}>
          <Col>
            <Title
              level={3}
              style={{ color: ZAMBIAN_COLORS.green, marginBottom: 0 }}
            >
              Account Management
            </Title>
            <Text type="secondary">
              Adjust account balance and record expenses
            </Text>
          </Col>
          <Col flex="auto" />

          {/* Account Selector */}
          <Col span={8}>
            {accounts && accounts.length > 0 ? (
              <Select
                style={{ width: "100%" }}
                placeholder="Select Account"
                value={selectedAccount?.id}
                onChange={(accountId) => {
                  const account = accounts.find((acc) => acc.id === accountId);
                  setSelectedAccount(account);
                }}
              >
                {accounts.map((account) => (
                  <Option key={account.id} value={account.id}>
                    <Space>
                      <BankOutlined
                        style={{
                          color: account.is_default
                            ? ZAMBIAN_COLORS.green
                            : "#666",
                        }}
                      />
                      <span>
                        {account.name} {account.is_default ? "(Default)" : ""}
                      </span>
                    </Space>
                  </Option>
                ))}
              </Select>
            ) : (
              <Text type="warning">
                No accounts found. Please add accounts in Account Management.
              </Text>
            )}
          </Col>

          <Col>
            <Statistic
              title="Current Balance"
              value={selectedAccount?.balance || 0}
              // prefix={<DollarOutlined />}
              suffix="ZMW"
              valueStyle={{
                fontSize: 24,
                color:
                  (selectedAccount?.balance || 0) < 0
                    ? ZAMBIAN_COLORS.red
                    : ZAMBIAN_COLORS.green,
              }}
            />
          </Col>

          <Col>
            <Button
              icon={<SyncOutlined spin={refreshingAccounts} />}
              onClick={refreshAllAccounts}
              loading={refreshingAccounts}
              style={{ marginLeft: 16 }}
            >
              Refresh Balances
            </Button>
          </Col>
        </Row>

        {/* Show approval warning for restricted users */}
        {requiresApproval(user?.email, "Operational Cost", 1) && (
          <Alert
            message="Expense Approval Required"
            description={
              <span>
                <ExclamationCircleOutlined style={{ marginRight: 8 }} />
                Your operational cost expenses require administrator approval
                before being processed. You will receive a confirmation once
                submitted.
              </span>
            }
            type="warning"
            showIcon
            style={{ marginBottom: 16, marginTop: 24 }}
            closable
          />
        )}

        <Tabs defaultActiveKey="1" style={{ marginTop: 24 }}>
          <TabPane tab="Float Transactions" key="1">
            <Form
              form={form}
              layout="vertical"
              onFinish={handleSubmit}
              initialValues={{
                transaction_type: "Float Addition",
                transaction_date: moment(),
              }}
            >
              <Row gutter={16}>
                <Col span={8}>
                  <Form.Item
                    name="transaction_type"
                    label="Transaction Type"
                    rules={[{ required: true }]}
                  >
                    <Select>
                      {TRANSACTION_TYPES.map((type) => (
                        <Select.Option key={type.value} value={type.value}>
                          <div>
                            {type.icon}
                            <span style={{ marginLeft: 8 }}>{type.label}</span>
                          </div>
                        </Select.Option>
                      ))}
                    </Select>
                  </Form.Item>
                </Col>

                <Col span={8}>
                  <Form.Item
                    name="amount"
                    label="Amount"
                    rules={[
                      { required: true, message: "Please enter amount" },
                      {
                        type: "number",
                        min: 1,
                        message: "Amount must be at least ZMW 1",
                      },
                    ]}
                  >
                    <InputNumber
                      style={{ width: "100%" }}
                      placeholder="Enter amount"
                      min={1}
                      step={100}
                      formatter={(value) =>
                        `ZMW ${value}`.replace(/\B(?=(\d{3})+(?!\d))/g, ",")
                      }
                      parser={(value) => value.replace(/ZMW\s?|(,*)/g, "")}
                    />
                  </Form.Item>
                </Col>

                <Col span={8}>
                  <Form.Item
                    name="transaction_date"
                    label="Date"
                    rules={[{ required: true }]}
                  >
                    <DatePicker
                      format="YYYY-MM-DD"
                      style={{ width: "100%" }}
                      disabledDate={(current) => current > moment()}
                    />
                  </Form.Item>
                </Col>
              </Row>

              <Row gutter={16}>
                <Col span={8}>
                  <Form.Item
                    name="payment_method"
                    label="Payment Method"
                    rules={[{ required: true }]}
                  >
                    <Select>
                      {PAYMENT_METHODS.map((method) => (
                        <Select.Option key={method.value} value={method.value}>
                          <div>
                            {method.icon}
                            <span style={{ marginLeft: 8 }}>
                              {method.label}
                            </span>
                          </div>
                        </Select.Option>
                      ))}
                    </Select>
                  </Form.Item>
                </Col>

                <Col span={8}>
                  <Form.Item
                    name="reference_number"
                    label="Reference Number"
                    rules={[{ required: true }]}
                  >
                    <Input
                      autoComplete="off"
                      placeholder="e.g. Receipt #, Transfer ID"
                    />
                  </Form.Item>
                </Col>

                <Col span={8}>
                  <Form.Item name="notes" label="Notes">
                    <Input.TextArea placeholder="Additional details..." />
                  </Form.Item>
                </Col>
              </Row>

              <Form.Item>
                <Button
                  type="primary"
                  htmlType="submit"
                  loading={submitting}
                  block
                  size="large"
                  style={{
                    backgroundColor: ZAMBIAN_COLORS.green,
                    borderColor: ZAMBIAN_COLORS.green,
                  }}
                  disabled={!selectedAccount}
                >
                  {submitting ? "Processing..." : "Record Transaction"}
                </Button>
              </Form.Item>
            </Form>
          </TabPane>

          <TabPane tab="Transfer Funds" key="2">
            <Form
              form={transferForm}
              layout="vertical"
              onFinish={handleTransfer}
            >
              <Row gutter={16}>
                <Col span={8}>
                  <Form.Item
                    name="from_account"
                    label="From Account"
                    rules={[
                      {
                        required: true,
                        message: "Please select source account",
                      },
                    ]}
                  >
                    <Select placeholder="Select source account">
                      {accounts &&
                        accounts.map((account) => (
                          <Option key={account.id} value={account.id}>
                            {account.name} (Balance: K
                            {parseFloat(account.balance || 0).toFixed(2)})
                          </Option>
                        ))}
                    </Select>
                  </Form.Item>
                </Col>

                <Col span={8}>
                  <Form.Item
                    name="to_account"
                    label="To Account"
                    rules={[
                      {
                        required: true,
                        message: "Please select destination account",
                      },
                    ]}
                  >
                    <Select placeholder="Select destination account">
                      {accounts &&
                        accounts.map((account) => (
                          <Option key={account.id} value={account.id}>
                            {account.name}
                          </Option>
                        ))}
                    </Select>
                  </Form.Item>
                </Col>

                <Col span={8}>
                  <Form.Item
                    name="amount"
                    label="Amount to Transfer"
                    rules={[
                      { required: true, message: "Please enter amount" },
                      {
                        type: "number",
                        min: 1,
                        message: "Amount must be at least K1",
                      },
                    ]}
                  >
                    <InputNumber
                      style={{ width: "100%" }}
                      placeholder="Enter amount to transfer"
                      min={1}
                      step={100}
                      formatter={(value) =>
                        `K ${value}`.replace(/\B(?=(\d{3})+(?!\d))/g, ",")
                      }
                      parser={(value) => value.replace(/K\s?|(,*)/g, "")}
                    />
                  </Form.Item>
                </Col>
              </Row>

              <Row gutter={16}>
                <Col span={24}>
                  <Form.Item name="notes" label="Transfer Notes">
                    <Input.TextArea placeholder="Add notes about this transfer..." />
                  </Form.Item>
                </Col>
              </Row>

              <Form.Item>
                <Button
                  type="primary"
                  htmlType="submit"
                  loading={transferSubmitting}
                  block
                  size="large"
                  style={{
                    backgroundColor: ZAMBIAN_COLORS.orange,
                    borderColor: ZAMBIAN_COLORS.orange,
                  }}
                >
                  {transferSubmitting
                    ? "Processing Transfer..."
                    : "Transfer Funds"}
                </Button>
              </Form.Item>
            </Form>
          </TabPane>

          {/* <TabPane tab="Transaction History" key="3">
            <div style={{ marginBottom: 16 }}>
              <Row justify="space-between" align="middle">
                <Col>
                  <Text strong>
                    Recent transactions for{" "}
                    {selectedAccount?.name || "Selected Account"}
                  </Text>
                </Col>
                <Col>
                  <Button
                    onClick={fetchAccountTransactions}
                    loading={loadingTransactions}
                    icon={<SyncOutlined />}
                  >
                    Refresh
                  </Button>
                </Col>
              </Row>
            </div>

            <Table
              dataSource={transactions}
              loading={loadingTransactions}
              rowKey="id"
              pagination={{
                pageSize: 10,
                showSizeChanger: true,
                showTotal: (total, range) =>
                  `${range[0]}-${range[1]} of ${total} transactions`,
              }}
              columns={[
                {
                  title: "Date",
                  dataIndex: "transaction_date",
                  key: "transaction_date",
                  render: (date) => moment(date).format("DD MMM YYYY"),
                  sorter: (a, b) =>
                    moment(a.transaction_date).unix() -
                    moment(b.transaction_date).unix(),
                  defaultSortOrder: "descend",
                },
                {
                  title: "Type",
                  dataIndex: "transaction_type",
                  key: "transaction_type",
                  render: (type) => {
                    let color = "blue";
                    if (type === "Float Addition") color = "green";
                    else if (type === "Operational Cost") color = "red";
                    else if (type === "Account Transfer") color = "orange";

                    return <Tag color={color}>{type}</Tag>;
                  },
                },
                {
                  title: "Amount",
                  dataIndex: "amount",
                  key: "amount",
                  render: (amount, record) => {
                    let isPositive = false;
                    let prefix = "";

                    if (record.transaction_type === "Float Addition") {
                      isPositive = true;
                      prefix = "+";
                    } else if (record.transaction_type === "Operational Cost") {
                      isPositive = false;
                      prefix = "-";
                    } else if (record.transaction_type === "Account Transfer") {
                      // For transfers, check if this account is receiving or sending
                      isPositive = record.to_account === selectedAccount?.id;
                      prefix = isPositive ? "+" : "-";
                    }

                    return (
                      <span
                        style={{
                          color: isPositive
                            ? ZAMBIAN_COLORS.green
                            : ZAMBIAN_COLORS.red,
                          fontWeight: "bold",
                        }}
                      >
                        {prefix}ZMW{" "}
                        {parseFloat(amount || 0)
                          .toFixed(2)
                          .replace(/\B(?=(\d{3})+(?!\d))/g, ",")}
                      </span>
                    );
                  },
                  sorter: (a, b) => a.amount - b.amount,
                },
                {
                  title: "Details",
                  key: "details",
                  render: (_, record) => {
                    if (record.transaction_type === "Account Transfer") {
                      // Find account names for transfers
                      const fromAccount = accounts?.find(
                        (acc) => acc.id === record.from_account
                      );
                      const toAccount = accounts?.find(
                        (acc) => acc.id === record.to_account
                      );
                      const isIncoming =
                        record.to_account === selectedAccount?.id;

                      return (
                        <div>
                          <Text type="secondary" style={{ fontSize: "12px" }}>
                            {isIncoming ? "From: " : "To: "}
                            {isIncoming
                              ? fromAccount?.name || "Unknown Account"
                              : toAccount?.name || "Unknown Account"}
                          </Text>
                        </div>
                      );
                    }

                    // For non-transfer transactions, show payment method
                    return (
                      <Tag>
                        {record.payment_method
                          ?.replace(/_/g, " ")
                          .replace(/\b\w/g, (l) => l.toUpperCase()) || "N/A"}
                      </Tag>
                    );
                  },
                },
                {
                  title: "Reference",
                  dataIndex: "reference_number",
                  key: "reference_number",
                },
                {
                  title: "Notes",
                  dataIndex: "notes",
                  key: "notes",
                  ellipsis: true,
                  render: (notes) => notes || "-",
                },
                {
                  title: "New Balance",
                  dataIndex: "new_amount",
                  key: "new_amount",
                  render: (amount) => (
                    <Text strong>
                      ZMW{" "}
                      {parseFloat(amount || 0)
                        .toFixed(2)
                        .replace(/\B(?=(\d{3})+(?!\d))/g, ",")}
                    </Text>
                  ),
                },
              ]}
            />
          </TabPane> */}
        </Tabs>
      </Card>
    </div>
  );
};

export default FloatManagementPage;
